/* eslint-disable @angular-eslint/no-output-native */
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef,
  EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, TemplateRef, ViewChild
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkOverlayOrigin, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Subject, fromEvent, merge } from 'rxjs';
import { filter, startWith, switchMap, share, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'insight-popup-tooltip',
  templateUrl: './popup-tooltip.component.html',
  styleUrls: ['./popup-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkConnectedOverlay, NgTemplateOutlet]
})
export class PopupTooltipComponent implements OnDestroy, OnInit {

  // Only one tooltip should be visible at a time. Notifies every instance when
  // any tooltip opens so the previously opened one can close itself — otherwise
  // a tooltip whose close trigger never fires (e.g. the pointer skipped past it)
  // stays stuck open.
  private static opened$ = new Subject<PopupTooltipComponent>();

  @Input() overlayOrigin: CdkOverlayOrigin;
  @Input() noPadding: boolean;
  @Output() close = new EventEmitter<any>();
  @Output() open = new EventEmitter<any>();
  @ViewChild('dialog', { static: false }) dialog: ElementRef;

  // When calling <insight-popup-tooltip>, the content should be wrapped in an <ng-template>
  // for performance reasons where there can sometimes be thousands of items that have popups
  // on hover that would lag if instantiated all at once.
  //
  // Content wrapped in <ng-template> is only instantiated (and change detected)
  // while the tooltip is open. Plain projected content falls back to <ng-content>,
  // which Angular creates and change detects even while the overlay is closed.
  @ContentChild(TemplateRef) contentTemplate: TemplateRef<unknown>;

  public isOpened = false;
  public destroy$ = new Subject<any>();

  constructor(private changeDetectorRef: ChangeDetectorRef, private ngZone: NgZone) { }

  ngOnInit(): void {
    const overlayOriginEl = this.overlayOrigin.elementRef.nativeElement;

    // close this tooltip whenever a different one opens
    PopupTooltipComponent.opened$.pipe(
      filter(source => source !== this && this.isOpened),
      takeUntil(this.destroy$)
    ).subscribe(() => this.changeState(false));

    // These streams listen to document-level mousemove events. Run them outside the
    // Angular zone so mouse movement doesn't trigger app-wide change detection —
    // with thousands of tooltip instances on screen that made every mousemove expensive.
    // changeState() re-enters the zone when the tooltip actually opens or closes.
    this.ngZone.runOutsideAngular(() => {
      // open popup if mouse stopped in overlayOriginEl (for short time).
      // If user just quickly got over overlayOriginEl element - do not open
      const open$ = fromEvent(overlayOriginEl, 'mouseenter').pipe(
        filter(() => !this.isOpened),
        switchMap(enterEvent =>
          fromEvent(document, 'mousemove').pipe(
            startWith(enterEvent),
            filter((event: Event) => overlayOriginEl === event.target),
            // stop listening once we've opened, or once the mouse leaves the origin —
            // otherwise the document listener stays attached for the component's lifetime
            take(1),
            takeUntil(fromEvent(overlayOriginEl, 'mouseleave'))
          )
        ),
        share()
      );

      open$.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => this.changeState(true));

      // close once the pointer moves or presses outside the overlayOriginEl and dialog
      const pointerOutside$ = merge(
        fromEvent<MouseEvent>(document, 'mousemove'),
        fromEvent<MouseEvent>(document, 'mousedown')
      ).pipe(
        filter(event => this.isOutside(overlayOriginEl, event.target))
      );

      // Pressing a link or button in the dialog and releasing outside it starts a native
      // drag: no mousemove arrives while it runs, and dragend targets the dragged element,
      // so resolve the drop point from its coordinates instead.
      const dragEndedOutside$ = fromEvent<DragEvent>(document, 'dragend').pipe(
        filter(event => this.isOutside(overlayOriginEl, document.elementFromPoint(event.clientX, event.clientY)))
      );

      open$.pipe(
        switchMap(() =>
          merge(pointerOutside$, dragEndedOutside$).pipe(
            filter(() => this.isOpened),
            take(1)
          )
        ),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.changeState(false);
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  connectedOverlayDetach() {
    this.changeState(false);
  }

  private changeState(isOpened: boolean) {
    // called from streams running outside the Angular zone, so re-enter it
    // before emitting outputs and marking for check
    this.ngZone.run(() => {
      this.isOpened = isOpened;

      if (isOpened) {
        // tell every other instance to close before announcing this one opened
        PopupTooltipComponent.opened$.next(this);
        this.open.emit();
      } else {
        this.close.emit();
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  private isOutside(overlayOriginEl: HTMLElement, target: EventTarget | null): boolean {
    const node = target instanceof Node ? target : null;
    return !(overlayOriginEl.contains(node) || this.dialog?.nativeElement?.contains(node));
  }
}
